<template>
  <div class="dashboard-page">
    <div class="dashboard-page__toolbar">
      <CDropdown togglerText="Period">
        <CDropdownItem @click="onLast2WeeksClick">Dnevni unosi zadnja 2 tjedna</CDropdownItem>
        <CDropdownItem @click="onLast3MonthsClick">Tjedni prosjek zadnja 3 mjeseca</CDropdownItem>
        <CDropdownItem @click="onAllMonthsClick">Mjesecni prosjek od pocetka</CDropdownItem>
      </CDropdown>
      <Dropdown :title="selectedResolution.key" :items="resolutionDropdownOptions" />
      <CInput class="dashboard-page__date-input" type="date" :value="dateInput" @change="onFromDateChange" :max="dateInputMax" />
    </div>

    <CRow>
      <CCol lg="4" class="mb-4">
        <CCard class="dashboard-card">
          <CCardBody>
            <div class="dashboard-card__header">
              <h3 class="dashboard-card__title">Tezina</h3>
            </div>
            <div class="dashboard-card__chart">
              <CChartBar class="dashboard-card__chart-canvas" :labels="weightsChartData.x" :datasets="weightsChartData.y" :options="weightsChartData.options" />
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="4" class="mb-4">
        <CCard class="dashboard-card">
          <CCardBody>
            <div class="dashboard-card__header">
              <h3 class="dashboard-card__title">Hrana</h3>
            </div>
            <div class="dashboard-card__chart">
              <CChartBar class="dashboard-card__chart-canvas" :labels="caloriesChartData.x" :datasets="caloriesChartData.y" :options="caloriesChartData.options" />
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="4" class="mb-4">
        <CCard class="dashboard-card">
          <CCardBody>
            <div class="dashboard-card__header">
              <h3 class="dashboard-card__title">San</h3>
            </div>
            <div class="dashboard-card__chart">
              <CChartBar class="dashboard-card__chart-canvas" :labels="sleepChartData.x" :datasets="sleepChartData.y" :options="sleepChartData.options" />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import Dropdown from "./WeightHistory/WeightHistoryDropdown";
import { getWeightTrendColors } from "@/weightChartColors";
import { CChartBar } from "@coreui/vue-chartjs";
import moment from "moment";

const resolutionDropdownOptions = ["Dnevno", "Tjedno", "Mjesečno"].map((entry) => {
  return {
    key: entry,
    title: entry,
    isSelected: entry === "Tjedno",
    description: entry === "Dnevno" ? "dnevno" : entry === "Tjedno" ? "tjedni prosjeci" : "mjesecni prosjeci",
  };
});

const dateIsSunday = (date) => date.locale("en").weekday() === 0;

const getDaysBetweenDates = (from, to) => {
  const dates = [];
  while (from.isSameOrBefore(to)) {
    dates.push(from);
    from = moment(from).add(1, "days");
  }
  return dates;
};

const getIncludingDaysWithoutValue = (fromDate, data) => {
  const toDate = moment().startOf("day");
  return getDaysBetweenDates(fromDate, toDate).map((date) => {
    return {
      date,
      value:
        data
          .filter((entry) => date.isSame(entry.date))
          .map((entry) => entry.value)
          .pop() ?? "",
    };
  });
};

const getAverage = (arr) => arr.reduce((previous, current) => previous + current, 0) / arr.length;
const currentYear = new Date().getFullYear();
const getChartXAxisDateFormat = (date) => moment(date).format("DD.MM" + (moment(date).format("YYYY") == currentYear ? "" : ".YY"));

const getChartData = (data, groupResolution) => {
  const resultData = [];
  let currentGroup = [];

  for (let i = 0; i < data.length; i += 1) {
    const current = data[i];

    if (groupResolution === "Dnevno") {
      resultData.push({
        key: current.date.format("YYYY-MM-DD"),
        value: current.value ? current.value : null,
      });
    } else {
      currentGroup.push(current);

      const isLastItemInGroup =
        i === data.length - 1 ||
        (groupResolution === "Tjedno" && dateIsSunday(current.date)) ||
        (groupResolution === "Mjesečno" && data[i + 1].date.format("D") === "1");

      if (isLastItemInGroup) {
        const nonZeroValues = currentGroup.map((entry) => entry.value).filter((entry) => entry);
        const averageValue = getAverage(nonZeroValues);
        resultData.push({
          key: currentGroup[currentGroup.length - 1].date.format("YYYY-MM-DD"),
          value: averageValue ? averageValue : null,
        });
        currentGroup = [];
      }
    }
  }

  return resultData;
};

const createSharedTooltip = (unit, thresholdLabel) => ({
  mode: "index",
  intersect: false,
  backgroundColor: "#0f172a",
  titleFontColor: "#f8fafc",
  bodyFontColor: "#e2e8f0",
  xPadding: 12,
  yPadding: 10,
  callbacks: {
    label: (tooltipItem, data) => {
      const dataset = data.datasets[tooltipItem.datasetIndex];
      return dataset.type === "line" ? `${thresholdLabel}: ${tooltipItem.value} ${unit}` : `${dataset.label}: ${tooltipItem.value} ${unit}`;
    },
  },
});

export default {
  components: {
    CChartBar,
    Dropdown,
  },
  methods: {
    onFromDateChange(newValue) {
      this.dateFrom = newValue ? moment(newValue) : null;
    },
    onLast2WeeksClick() {
      this.dateFrom = moment().add(-2, "weeks");
      this.resolutionDropdownOptions.forEach((entry) => (entry.isSelected = entry.key === "Dnevno"));
    },
    onLast3MonthsClick() {
      this.dateFrom = moment().add(-3, "months");
      this.resolutionDropdownOptions.forEach((entry) => (entry.isSelected = entry.key === "Tjedno"));
    },
    onAllMonthsClick() {
      this.dateFrom = null;
      this.resolutionDropdownOptions.forEach((entry) => (entry.isSelected = entry.key === "Mjesečno"));
    },
  },
  data() {
    return {
      dateFrom: moment().add(-3, "months"),
      resolutionDropdownOptions,
      dateInputMax: moment().add(-1, "days").format("YYYY-MM-DD"),
    };
  },
  computed: {
    maxCalories() {
      return this.$store.state.maxCalories;
    },
    minSleep() {
      return this.$store.state.minSleep;
    },
    dateInput() {
      return moment(this.fromDate).format("YYYY-MM-DD");
    },
    fromDate() {
      return this.$store.state.weightHistory.filter((entry) => !this.dateFrom || entry.date > this.dateFrom)[0].date;
    },
    weightsChartData() {
      const storeData = this.$store.state.weightHistory.map((entry) => ({ date: entry.date, value: entry.value }));
      const data = getIncludingDaysWithoutValue(this.fromDate, storeData);
      const chartData = getChartData(data, this.selectedResolution.key);
      const values = chartData.filter((entry) => entry.value).map((entry) => entry.value);
      const chartValues = chartData.map((entry) => (entry.value ? Number(entry.value.toFixed(1)) : null));
      const min = values.length ? Math.floor(Math.min(...values) / 5) * 5 : 0;

      return {
        x: chartData.map((entry) => getChartXAxisDateFormat(entry.key)),
        y: [
          {
            label: "Tezina",
            data: chartValues,
            backgroundColor: getWeightTrendColors(chartValues),
            borderRadius: 6,
            barPercentage: 0.72,
            categoryPercentage: 0.74,
          },
        ],
        options: {
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: {
            mode: "index",
            intersect: false,
            backgroundColor: "#0f172a",
            titleFontColor: "#f8fafc",
            bodyFontColor: "#e2e8f0",
            displayColors: false,
            xPadding: 12,
            yPadding: 10,
            callbacks: {
              label: (tooltipItem) => `${tooltipItem.value} kg`,
            },
          },
          scales: {
            xAxes: [
              {
                gridLines: { display: false, drawBorder: false },
                ticks: { fontColor: "#94a3b8", maxTicksLimit: 8 },
              },
            ],
            yAxes: [
              {
                gridLines: { color: "#e8eef5", drawBorder: false },
                ticks: {
                  maxTicksLimit: 6,
                  min,
                  fontColor: "#94a3b8",
                  callback: (value) => `${value} kg`,
                },
              },
            ],
          },
        },
      };
    },
    caloriesChartData() {
      const storeData = this.$store.state.calories.map((entry) => ({ date: moment(entry.date), value: entry.value }));
      const data = getIncludingDaysWithoutValue(this.fromDate, storeData);
      const chartData = getChartData(data, this.selectedResolution.key);

      return {
        x: chartData.map((entry) => getChartXAxisDateFormat(entry.key)),
        y: [
          {
            label: "Hrana",
            data: chartData.map((entry) => (entry.value ? parseInt(entry.value, 10) : 0)),
            backgroundColor: chartData.map((entry) => (entry.value > this.maxCalories ? "#dc2626" : "#0f766e")),
            borderRadius: 6,
            barPercentage: 0.72,
            categoryPercentage: 0.74,
          },
          {
            type: "line",
            label: "Limit",
            data: chartData.map(() => this.maxCalories),
            borderColor: "#d97706",
            borderDash: [6, 6],
            borderWidth: 2,
            pointRadius: 0,
            fill: false,
          },
        ],
        options: {
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: createSharedTooltip("kcal", "Limit"),
          scales: {
            xAxes: [
              {
                gridLines: { display: false, drawBorder: false },
                ticks: { fontColor: "#94a3b8", maxTicksLimit: 8 },
              },
            ],
            yAxes: [
              {
                gridLines: { color: "#e8eef5", drawBorder: false },
                ticks: {
                  maxTicksLimit: 6,
                  min: 0,
                  fontColor: "#94a3b8",
                  callback: (value) => `${value} kcal`,
                },
              },
            ],
          },
        },
      };
    },
    sleepChartData() {
      const storeData = this.$store.state.sleep.map((entry) => ({ date: moment(entry.date), value: entry.value }));
      const data = getIncludingDaysWithoutValue(this.fromDate, storeData);
      const chartData = getChartData(data, this.selectedResolution.key);
      const values = chartData.filter((entry) => entry.value).map((entry) => entry.value);
      const min = values.length && Math.min(...values) >= 4 ? 4 : 0;

      return {
        x: chartData.map((entry) => getChartXAxisDateFormat(entry.key)),
        y: [
          {
            label: "San",
            data: chartData.map((entry) => (entry.value ? Number(entry.value.toFixed(1)) : 0)),
            backgroundColor: chartData.map((entry) => (entry.value <= this.minSleep ? "#dc2626" : "#0f766e")),
            borderRadius: 6,
            barPercentage: 0.72,
            categoryPercentage: 0.74,
          },
          {
            type: "line",
            label: "Minimum",
            data: chartData.map(() => this.minSleep),
            borderColor: "#d97706",
            borderDash: [6, 6],
            borderWidth: 2,
            pointRadius: 0,
            fill: false,
          },
        ],
        options: {
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: createSharedTooltip("h", "Minimum"),
          scales: {
            xAxes: [
              {
                gridLines: { display: false, drawBorder: false },
                ticks: { fontColor: "#94a3b8", maxTicksLimit: 8 },
              },
            ],
            yAxes: [
              {
                gridLines: { color: "#e8eef5", drawBorder: false },
                ticks: {
                  maxTicksLimit: 6,
                  min,
                  fontColor: "#94a3b8",
                  callback: (value) => `${value} h`,
                },
              },
            ],
          },
        },
      };
    },
    selectedResolution() {
      return this.resolutionDropdownOptions.find((entry) => entry.isSelected);
    },
  },
};
</script>

<style scoped>
.dashboard-page {
  display: grid;
  gap: 1.5rem;
}

.dashboard-page__toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.dashboard-page__date-input {
  width: auto;
  min-width: 180px;
}

.dashboard-card {
  height: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: none;
}

.dashboard-card .card-body {
  display: flex;
  flex-direction: column;
  min-height: 360px;
  padding: 1.5rem;
}

.dashboard-card__chart {
  position: relative;
  flex: 0 0 auto;
  height: clamp(220px, 28vh, 260px);
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.dashboard-card__chart-canvas {
  height: 100%;
  width: 100%;
  min-width: 0;
}

.dashboard-card__chart-canvas /deep/ canvas {
  height: 100% !important;
  width: 100% !important;
  max-width: 100%;
  display: block;
}

.dashboard-card__header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.dashboard-card__title {
  margin: 0;
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 700;
}

@media (max-width: 767px) {
  .dashboard-page__toolbar {
    gap: 0.625rem;
  }

  .dashboard-card {
    border-radius: 14px;
  }

  .dashboard-card .card-body {
    min-height: 340px;
    padding: 1rem;
  }

  .dashboard-card__chart {
    height: 210px;
  }

  .dashboard-card__header {
    margin-bottom: 1rem;
  }

  .dashboard-card__title {
    font-size: 1.05rem;
  }
}
</style>
